import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import '../styles/AboutSection.css';
import about from '../assets/images/eks-about.png';
import successImg1 from '../assets/images/success-card-img1.jpg';
import successImg2 from '../assets/images/success-card-img2.jpg';
import successImg3 from '../assets/images/success-card-img3.jpg';
import successImg4 from '../assets/images/success-card-img4.jpg';
import successImg5 from '../assets/images/success-card-img5.jpg';
import successImg6 from '../assets/images/success-card-img6.jpg';
import successImg7 from '../assets/images/success-card-img7.jpg';
import successImg8 from '../assets/images/success-card-img8.jpg';
import successImg9 from '../assets/images/success-card-img9.jpg';

const AboutSection = () => {
  const [activeCard, setActiveCard] = useState(null);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"]
  });

  // Success cards data with imported images and vibrant colors
  const successData = [
    { id: 1, text: '120+ Standard Quality Checks ', color: '#4169E1', image: successImg1 },
    { id: 2, text: 'Construction on Your Budget ', color: '#FF4444', image: successImg2 },
    { id: 3, text: 'No Hidden Charges ', color: '#10B981', image: successImg3 },
    { id: 4, text: 'Stress Free Construction ', color: '#F59E0B', image: successImg4 },
    { id: 5, text: 'Creative Design ', color: '#8B5CF6', image: successImg5 },
    { id: 6, text: 'On time Delivery ', color: '#EC4899', image: successImg6 },
    { id: 7, text: 'Daily Progress Update ', color: '#14B8A6', image: successImg7 },
    { id: 8, text: 'No Subcontracts ', color: '#F97316', image: successImg8 },
    { id: 9, text: 'End to End Construction ', color: '#6366F1', image: successImg9 }
  ];

  // Generate card positions with zigzag layout (3 per row)
  const getCardPositions = () => {
    const positions = [];
    const rowSize = 3;
    const cardWidth = 280;
    const cardSpacing = 60;
    const rowHeight = 420;
    const containerWidth = 1200;
    
    successData.forEach((card, index) => {
      const rowIndex = Math.floor(index / rowSize);
      const colIndex = index % rowSize;
      const isEvenRow = rowIndex % 2 === 0;
      
      let x;
      if (isEvenRow) {
        x = (containerWidth - (rowSize * cardWidth + (rowSize - 1) * cardSpacing)) / 2 + 
            colIndex * (cardWidth + cardSpacing) + cardWidth / 2;
      } else {
        x = (containerWidth - (rowSize * cardWidth + (rowSize - 1) * cardSpacing)) / 2 + 
            (rowSize - 1 - colIndex) * (cardWidth + cardSpacing) + cardWidth / 2;
      }
      
      const y = rowIndex * rowHeight + 150;
      positions.push({ x, y, cardId: card.id, rowIndex, colIndex });
    });
    
    return positions;
  };

  const cardPositions = getCardPositions();

  // Generate SVG path through all cards
  const generatePath = () => {
    if (cardPositions.length === 0) return "";
    let path = `M${cardPositions[0].x},${cardPositions[0].y}`;
    
    for (let i = 1; i < cardPositions.length; i++) {
      const current = cardPositions[i];
      const previous = cardPositions[i - 1];
      
      if (current.rowIndex !== previous.rowIndex) {
        const midY = (current.y + previous.y) / 2;
        path += ` Q${previous.x},${midY} ${current.x},${current.y}`;
      } else {
        path += ` L${current.x},${current.y}`;
      }
    }
    return path;
  };

  // Ball position based on scroll
  const ballX = useTransform(scrollYProgress, 
    cardPositions.map((_, i) => i / (cardPositions.length - 1)),
    cardPositions.map(pos => pos.x)
  );
  
  const ballY = useTransform(scrollYProgress, 
    cardPositions.map((_, i) => i / (cardPositions.length - 1)),
    cardPositions.map(pos => pos.y)
  );

  const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);

  // Update active card based on scroll
  useEffect(() => {
    return scrollYProgress.onChange(progress => {
      const cardIndex = Math.floor(progress * cardPositions.length);
      const cardId = cardPositions[cardIndex]?.cardId;
      setActiveCard(cardId || null);
    });
  }, [scrollYProgress, cardPositions]);

  // Group cards into rows of 3
  const rows = [];
  const rowSize = 3;
  for (let i = 0; i < successData.length; i += rowSize) {
    rows.push(successData.slice(i, i + rowSize));
  }
  const svgHeight = Math.ceil(successData.length / 3) * 420 + 200;

  return (
    <main style={{ background: 'white', minHeight: '100vh' }}>
      <section style={{ padding: '4rem 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Our Story Section */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center', marginBottom: '4rem' }}>
              <div>
                <h2 style={{ color: '#0f02ffb3', fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '2rem' }}>
                  <span style={{ color: '#4169E1', fontWeight: 'bold' }}>Our Story</span>
                </h2>
                <p style={{ textAlign: 'justify', color: '#666', fontSize: '1.1rem', lineHeight: '1.7', marginBottom: '1.5rem' }}>
                  <span style={{ color: 'blue' }}>e</span>
                  <span style={{ color: 'red' }}>k</span>
                  <span style={{ color: 'blue' }}>s</span>{' '}
                  <span style={{ color: 'blue' }}>construction</span> was founded to help people build spaces to live, work and make memories in.
                </p>
                <p style={{ textAlign: 'justify', color: '#666', fontSize: '1.1rem', lineHeight: '1.7' }}>
                  With a team of highly skilled architects, engineers, project managers and construction workers, we ensure every project is executed with precision.
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <img
                  src={about}
                  alt="About eks Construction"
                  style={{ width: '100%', maxWidth: '600px', height: '400px', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)', objectFit: 'cover' }}
                />
              </div>
            </div>

            {/* Vision and Mission */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '70px', margin: '40px auto 4rem', flexWrap: 'wrap', maxWidth: '1100px' }}>
              <div style={{ background: '#fff', borderTop: '4px solid #4169E1', padding: '32px 24px', boxShadow: '0 8px 32px rgba(65, 105, 225, 0.08)', borderRadius: '10px', width: '500px', minWidth: '280px', textAlign: 'justify' }}>
                <h2 style={{ color: '#4169E1', marginTop: 0, marginBottom: '16px', fontSize: '2.3rem', fontWeight: 700 }}>Our Vision</h2>
                <p style={{ color: '#666', lineHeight: '1.7' }}>
                  EKS Construction has developed a comprehensive, future-ready system that addresses every challenge in the construction journey.
                </p>
              </div>
              <div style={{ background: '#fff', borderTop: '4px solid #FF4444', padding: '32px 24px', boxShadow: '0 8px 32px rgba(65, 105, 225, 0.08)', borderRadius: '10px', width: '500px', minWidth: '280px', textAlign: 'justify' }}>
                <h2 style={{ color: '#FF4444', marginTop: 0, marginBottom: '16px', fontSize: '2.3rem', fontWeight: 700 }}>Our Mission</h2>
                <p style={{ color: '#666', lineHeight: '1.7' }}>
                  EKS Construction recognizes significant challenges and is committed to bridging gaps through innovation and transparency.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section ref={containerRef} style={{ position: 'relative', padding: '60px 20px', textAlign: 'center', minHeight: '150vh', background: '#f8f9fa' }}>
        <motion.h2 
          style={{ fontSize: '48px', fontWeight: 700, color: '#4169E1', marginBottom: '10px' }}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Reason Behind Our Success
        </motion.h2>
        
        <motion.p 
          style={{ fontSize: '20px', color: '#6b7280', marginBottom: '60px' }}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Excellence in every aspect of construction
        </motion.p>

        {/* Timeline Content */}
        <div style={{ position: 'relative', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
          {/* SVG Path and Ball */}
          <svg 
            style={{ position: 'absolute', top: 100, left: 0, zIndex: 1, pointerEvents: 'none' }}
            width="100%" 
            height={`${svgHeight}px`}
            viewBox={`0 0 1200 ${svgHeight}`}
          >
            <defs>
              <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: "#4169E1", stopOpacity: 1 }} />
              <stop offset="50%" style={{ stopColor: "#8B5CF6", stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: "#FF4444", stopOpacity: 1 }} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <filter id="shadow">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.3"/>
              </filter>
            </defs>
            
            <motion.path
              d={generatePath()}
              stroke="url(#pathGradient)"
              strokeWidth="5"
              fill="none"
              strokeDasharray="15,8"
              style={{ pathLength }}
              filter="url(#glow)"
              strokeLinecap="round"
            />
            
            <motion.g style={{ x: ballX, y: ballY }}>
              <motion.circle 
                r="20" 
                fill="url(#pathGradient)" 
                stroke="#fff" 
                strokeWidth="4" 
                filter="url(#glow)"
                animate={{ 
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              />
              <circle r="12" fill="#fff" opacity="0.95" />
              <motion.circle 
                r="6" 
                fill="url(#pathGradient)"
                animate={{ 
                  scale: [1, 1.2, 1],
                }}
                transition={{ 
                  duration: 1, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              />
            </motion.g>
          </svg>

          {/* Timeline Cards */}
          <div style={{ position: 'relative', zIndex: 2 }}>
            {rows.map((rowCards, rowIndex) => (
              <div
                key={rowIndex}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '60px',
                  marginBottom: '100px',
                  padding: '0 20px',
                  justifyItems: 'center',
                  direction: rowIndex % 2 === 1 ? 'rtl' : 'ltr'
                }}
              >
                {rowCards.map((card) => (
                  <motion.div
                    key={card.id}
                    style={{
                      background: activeCard === card.id ? 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)' : '#fff',
                      boxShadow: activeCard === card.id ? '0 30px 60px rgba(0,0,0,0.25)' : '0 10px 25px rgba(0,0,0,0.1)',
                      width: '100%',
                      maxWidth: '280px',
                    padding: '20px',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      transition: 'all 0.4s ease',
                      position: 'relative',
                      zIndex: 3,
                      direction: 'ltr',
                      transform: activeCard === card.id ? 'translateY(-20px) scale(1.08)' : 'translateY(0) scale(1)',
                      border: activeCard === card.id ? '4px solid' : '2px solid transparent',
                      borderImage: activeCard === card.id ? 'linear-gradient(135deg, #4169E1, #8B5CF6, #EC4899, #F59E0B) 1' : 'none'
                    }}
                    initial={{ opacity: 0, scale: 0.8, y: 50 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
                    onMouseEnter={(e) => {
                      if (activeCard !== card.id) {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeCard !== card.id) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
                      }
                    }}
                  >
                    {activeCard === card.id && (
                      <div style={{
                        position: 'absolute',
                        top: '-6px',
                        left: '-6px',
                        right: '-6px',
                        bottom: '-6px',
                        background: 'linear-gradient(135deg, #4169E1, #8B5CF6, #EC4899)',
                        zIndex: -1,
                        opacity: 0.4,
                        filter: 'blur(8px)'
                      }} />
                    )}
                    <img 
                      src={card.image}
                      alt={card.text}
                      style={{
                        width: '100%',
                        height: '180px',
                        objectFit: 'cover',
                        borderRadius: '15px',
                        marginBottom: '15px',
                        display: 'block',
                        background: '#eee',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
                      }}
                    />
                    <motion.div
                      style={{
                        width: '50px',
                        height: '50px',
                        margin: '0 auto 15px',
                        background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}dd 100%)`,
                        color: '#fff',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        transition: 'all 0.3s ease',
                        boxShadow: `0 4px 20px ${card.color}66`,
                        border: '3px solid white'
                      }}
                      animate={{ 
                        scale: activeCard === card.id ? 1.5 : 1,
                        rotate: activeCard === card.id ? 360 : 0
                      }}
                      transition={{ duration: 0.6, type: "spring", stiffness: 200 }}
                    >
                      {card.id}
                    </motion.div>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#1f2937',
                      lineHeight: '1.4',
                      margin: 0,
                      textAlign: 'center'
                    }}>
                      {card.text}
                    </h3>
                  </motion.div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes borderPulse {
          0%, 100% { 
            opacity: 0.4;
            transform: scale(1);
          }
          50% { 
            opacity: 0.7;
            transform: scale(1.03);
          }
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        @media (max-width: 968px) {
          [style*="grid-template-columns: repeat(3, 1fr)"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        @media (max-width: 768px) {
          [style*="grid-template-columns: repeat(3, 1fr)"],
          [style*="grid-template-columns: repeat(2, 1fr)"] {
            grid-template-columns: 1fr !important;
          }
          svg {
            display: none !important;
          }
        }
      `}</style>
    </main>
  );
};

export default AboutSection;